import * as queryString from 'query-string';

export const ImportedEventTabIds = {
    PLAYERS: 1,
    RESULTS: 2,
};

export const TAB_URL_PARAM = 't';

const DEFAULT_IMPORTED_EVENT_TAB_ID = ImportedEventTabIds.PLAYERS;

const IMPORTED_EVENT_TAB_IDS = Object.values(ImportedEventTabIds);

// Returns null when the URL carries no usable tab param, which is what lets a shared link reproduce
// the address bar rather than invent a tab for a URL that never had one.
export const getImportedEventTabIdFromSearch = (search) => {
    const parsedTabId = parseInt(queryString.parse(search ?? '')[TAB_URL_PARAM], 10);

    return IMPORTED_EVENT_TAB_IDS.includes(parsedTabId) ? parsedTabId : null;
};

export const resolveImportedEventTabId = (search) => (
    getImportedEventTabIdFromSearch(search) ?? DEFAULT_IMPORTED_EVENT_TAB_ID
);

export const buildImportedEventTabShareSearch = (tabId) => (
    IMPORTED_EVENT_TAB_IDS.includes(tabId) ? `?${queryString.stringify({ [TAB_URL_PARAM]: tabId })}` : ''
);

const GLOBAL_PARAMS = [TAB_URL_PARAM];

// Params in neither this list nor GLOBAL_PARAMS pass through untouched on every tab switch —
// only a param explicitly owned by one tab is stripped when navigating to a different one.
const TAB_OWNED_PARAMS = {
    [ImportedEventTabIds.PLAYERS]: [],
    // `drawId` is singular here on purpose: the Results endpoint takes a single `drawId`, unlike the
    // Players tab's `drawIds`. Owned by this tab so switching to Players drops it. `position` is
    // owned here too - it only ever deep-links in from the league Players tab's Win/Loss cell, so
    // it must not persist once the user navigates back to Players.
    [ImportedEventTabIds.RESULTS]: ['search', 'drawId', 'position'],
};

const findOwningTabId = (paramName) => Object.keys(TAB_OWNED_PARAMS)
    .find((tabId) => TAB_OWNED_PARAMS[tabId].includes(paramName));

export const stripForeignParams = (queryParams, targetTabId) => {
    const targetTabIdString = String(targetTabId);

    return Object.keys(queryParams ?? {}).reduce((keptParams, paramName) => {
        const isGlobal = GLOBAL_PARAMS.includes(paramName);
        const owningTabId = findOwningTabId(paramName);
        const isOwnedByTarget = owningTabId === targetTabIdString;
        const isUnowned = owningTabId === undefined;

        if (isGlobal || isOwnedByTarget || isUnowned) {
            return { ...keptParams, [paramName]: queryParams[paramName] };
        }

        return keptParams;
    }, {});
};

// `sort: false` keeps the tab id first in the URL. query-string sorts keys alphabetically by
// default, which would put `search` (and `position`/`drawId`) ahead of `t` and bury the parameter
// that decides which tab the link actually opens.
export const buildResultsSearchDeepLinkHref = (fullName, options) => {
    const { position, drawId } = options ?? {};
    const params = {
        [TAB_URL_PARAM]: ImportedEventTabIds.RESULTS,
        search: fullName,
    };

    if (typeof position === 'string' && position) {
        params.position = position;
    }

    if (drawId !== null && drawId !== undefined) {
        params.drawId = drawId;
    }

    return `?${queryString.stringify(params, { sort: false })}`;
};
